/**
 * VoucherDetailDialog — formal Tally-style voucher preview.
 *
 * Opens from a per-row eye icon on /vouchers. Renders the full slip with:
 *   • Company header + voucher title (Cash Payment / Bank Payment / Salary /…)
 *   • Voucher number, date, payment mode
 *   • Double-entry ledger table (Dr / Cr with amounts, right-aligned INR)
 *   • Amount in words (Indian numbering)
 *   • Section 40A(3) warning banner when the voucher is disallowed
 *   • Narration (if any)
 *   • Signature lines (Prepared / Approved / Received)
 *   • Actions: Print PDF, Open source daily sheet
 *
 * The dialog fetches fresh detail via `api.getVoucher(id)` so we always
 * render the latest debit_ledger / credit_ledger / amount_in_words that
 * the server computed — no risk of the frontend drifting from the CA's
 * exported Tally XML.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Printer, ExternalLink, AlertTriangle, Loader2 } from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { api, fmtMoney } from '../lib/api';


function fmtInrPlain(v) {
    // 12,34,567.89 — same Indian grouping but without any currency symbol,
    // so the table column can prepend ₹ or leave the header text as "Debit (₹)".
    const n = Number(v || 0);
    const s = fmtMoney(n) || '';
    // fmtMoney already outputs "₹1,23,456.78" — strip the ₹ so the column can be
    // pure numbers.
    return s.replace(/^₹\s*/, '');
}


export function VoucherDetailDialog({ voucherId, open, onOpenChange }) {
    const [voucher, setVoucher] = useState(null);
    const [loading, setLoading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const nav = useNavigate();

    useEffect(() => {
        if (!open || !voucherId) {
            setVoucher(null);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const v = await api.getVoucher(voucherId);
                if (!cancelled) setVoucher(v);
            } catch (e) {
                if (!cancelled) {
                    toast.error(e?.response?.data?.detail || 'Could not load voucher.');
                    onOpenChange(false);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [open, voucherId, onOpenChange]);

    const handlePrint = async () => {
        if (!voucherId) return;
        setPrinting(true);
        try {
            await api.printVoucher(voucherId);
            toast.success('Print dialog opened in new window.');
        } catch (e) {
            toast.error(e?.message || 'Could not open print dialog.');
        } finally {
            setPrinting(false);
        }
    };

    const openSourceSheet = () => {
        const sheetId = voucher?.source_sheet?.id;
        if (!sheetId) return;
        onOpenChange(false);
        nav(`/daily-sheet?open=${encodeURIComponent(sheetId)}`);
    };

    const disallowed = voucher?.compliance_flag === 'SECTION_40A3_DISALLOWED';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-2xl max-h-[90vh] overflow-y-auto"
                data-testid="voucher-detail-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-lg">
                        {loading ? 'Loading voucher…' : (voucher?.title || 'Voucher')}
                    </DialogTitle>
                    <DialogDescription>
                        {loading ? '\u00a0' : `Voucher No. ${voucher?.voucher_number || '—'} · ${voucher?.date || ''}`}
                    </DialogDescription>
                </DialogHeader>

                {loading || !voucher ? (
                    <div className="space-y-3 py-4">
                        <Skeleton className="h-6 w-1/2" />
                        <Skeleton className="h-32" />
                        <Skeleton className="h-16" />
                    </div>
                ) : (
                    <div className="space-y-4 font-serif">
                        {/* ── Formal slip layout ─────────────────────── */}
                        <div className="rounded-md border border-border bg-background p-5">
                            {/* Company header */}
                            <div className="text-center pb-2 border-b border-border">
                                <div className="text-xl font-semibold tracking-wide">
                                    {voucher.company_name || 'EaseMyBill'}
                                </div>
                                <div
                                    className="text-sm font-medium underline underline-offset-4 mt-2"
                                    data-testid="voucher-title"
                                >
                                    {voucher.title}
                                </div>
                            </div>

                            {/* Voucher meta strip */}
                            <div className="grid grid-cols-3 gap-2 text-xs mt-4 mb-4">
                                <div>
                                    <div className="uppercase text-[9px] tracking-wide text-muted-foreground">Voucher No.</div>
                                    <div className="font-semibold text-sm font-mono">{voucher.voucher_number}</div>
                                </div>
                                <div className="text-center">
                                    <div className="uppercase text-[9px] tracking-wide text-muted-foreground">Date</div>
                                    <div className="font-semibold text-sm">{voucher.date}</div>
                                </div>
                                <div className="text-right">
                                    <div className="uppercase text-[9px] tracking-wide text-muted-foreground">Payment Mode</div>
                                    <div className="font-semibold text-sm">{(voucher.payment_mode || '—').toUpperCase()}</div>
                                </div>
                            </div>

                            {/* Double-entry ledger */}
                            <table className="w-full text-sm border border-border" data-testid="voucher-ledger">
                                <thead className="bg-muted/50 text-xs">
                                    <tr>
                                        <th className="border border-border px-3 py-2 text-left font-medium">Particulars</th>
                                        <th className="border border-border px-3 py-2 text-right font-medium w-28">Debit (₹)</th>
                                        <th className="border border-border px-3 py-2 text-right font-medium w-28">Credit (₹)</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="border border-border px-3 py-2 align-top">
                                            <div><span className="font-semibold">Dr</span>&nbsp;&nbsp;{voucher.debit_ledger}</div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5">
                                                Being payment to {voucher.party || '—'}
                                            </div>
                                        </td>
                                        <td className="border border-border px-3 py-2 text-right font-mono tabular-nums" data-testid="voucher-debit-amount">
                                            {fmtInrPlain(voucher.amount)}
                                        </td>
                                        <td className="border border-border px-3 py-2">&nbsp;</td>
                                    </tr>
                                    <tr>
                                        <td className="border border-border px-3 py-2 pl-8">
                                            <span className="font-semibold">Cr</span>&nbsp;&nbsp;{voucher.credit_ledger}
                                        </td>
                                        <td className="border border-border px-3 py-2">&nbsp;</td>
                                        <td className="border border-border px-3 py-2 text-right font-mono tabular-nums" data-testid="voucher-credit-amount">
                                            {fmtInrPlain(voucher.amount)}
                                        </td>
                                    </tr>
                                    <tr className="bg-muted/40">
                                        <td className="border border-border px-3 py-2 text-right font-semibold">Total</td>
                                        <td className="border border-border px-3 py-2 text-right font-mono tabular-nums font-semibold">
                                            {fmtInrPlain(voucher.amount)}
                                        </td>
                                        <td className="border border-border px-3 py-2 text-right font-mono tabular-nums font-semibold">
                                            {fmtInrPlain(voucher.amount)}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>

                            {/* Amount in words */}
                            <div className="mt-3 text-sm italic" data-testid="voucher-in-words">
                                <span className="font-semibold not-italic">Amount in words:</span>{' '}
                                {voucher.amount_in_words}
                            </div>

                            {/* Section 40A(3) warning */}
                            {disallowed && (
                                <div
                                    className="mt-3 rounded-md border-l-2 border-red-600 bg-red-50/60 dark:bg-red-950/30 px-3 py-2 text-xs text-red-800 dark:text-red-300 flex gap-2"
                                    data-testid="voucher-40a3-warning"
                                >
                                    <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                                    <div>
                                        <div className="font-semibold">Section 40A(3) — Disallowed</div>
                                        Cash payment exceeds ₹10,000/day/category. This expense
                                        cannot be claimed as a business deduction.
                                    </div>
                                </div>
                            )}

                            {/* Narration */}
                            {voucher.narration && (
                                <div className="mt-3 rounded-md bg-muted/40 border-l-2 border-border px-3 py-2 text-sm">
                                    <span className="font-semibold">Narration:</span> {voucher.narration}
                                </div>
                            )}

                            {/* Source line */}
                            {voucher.source_sheet?.date && (
                                <div className="mt-3 text-xs text-muted-foreground">
                                    Auto-generated from Daily Sheet dated{' '}
                                    <span className="font-medium text-foreground">
                                        {voucher.source_sheet.date}
                                    </span>
                                </div>
                            )}

                            {/* Signature strip */}
                            <div className="mt-14 grid grid-cols-3 gap-4 text-center text-xs">
                                <div>
                                    <div className="border-t border-foreground mx-4 mb-1" />
                                    Prepared By
                                </div>
                                <div>
                                    <div className="border-t border-foreground mx-4 mb-1" />
                                    Approved By
                                </div>
                                <div>
                                    <div className="border-t border-foreground mx-4 mb-1" />
                                    Received By
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:justify-between">
                    <Button
                        variant="outline"
                        onClick={openSourceSheet}
                        disabled={!voucher?.source_sheet?.id}
                        className="gap-1"
                        data-testid="voucher-open-sheet"
                    >
                        <ExternalLink size={14} />
                        {voucher?.source_sheet?.id
                            ? `Open source daily sheet`
                            : 'No source sheet'}
                    </Button>
                    <Button
                        onClick={handlePrint}
                        disabled={!voucher || printing}
                        className="gap-1"
                        data-testid="voucher-print"
                    >
                        {printing ? (
                            <>
                                <Loader2 size={14} className="animate-spin" /> Preparing…
                            </>
                        ) : (
                            <>
                                <Printer size={14} /> Print / Save PDF
                            </>
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
